import { Button, Card, CardContent, CardTitle, Separator } from '@repo/shadcn';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { HeaderBreadcrumb } from '@components/ui';
import { SectionError } from '@components/ui/error';
import { SectionLoader } from '@components/ui/loader';

import { Route } from '@/constants';
import { formatCurrencyInBDT } from '@/utils';

import { useSalesReturnDetails } from './sales-return-details.hook';

export const SalesReturnDetails = () => {
    const { data, isLoading, isError, error } = useSalesReturnDetails();

    if (isLoading) {
        return <SectionLoader />;
    }

    if (isError) {
        return (
            <SectionError error={error} fallbackPath={Route.SALES_RETURNS} />
        );
    }

    const total = data?.data?.items?.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0,
    );

    const renderItems = () => {
        if (isLoading) {
            return <div>Loading items...</div>;
        }

        if (isError) {
            return <div>Something went wrong!</div>;
        }

        return data?.data?.items.map((item) => {
            return (
                <div key={item.id} className="grid grid-cols-10 gap-4 text-sm">
                    <div className="col-span-4 font-medium">
                        {item.product_name}
                    </div>
                    <div className="col-span-2 text-right">
                        {formatCurrencyInBDT(item.price)}
                    </div>
                    <div className="col-span-2 text-right">{item.quantity}</div>
                    <div className="col-span-2 text-right font-medium">
                        {formatCurrencyInBDT(item.price * item.quantity)}
                    </div>
                </div>
            );
        });
    };

    return (
        <Card className={'space-y-4 p-4'}>
            <HeaderBreadcrumb
                items={[
                    {
                        label: 'Sales Return',
                        href: Route.SALES_RETURNS,
                    },
                    {
                        label: isLoading
                            ? 'Loading...'
                            : `${data?.data?.invoice_number ?? 'Sales Return Details'}`,
                    },
                ]}
                showHomeLink={true}
            />

            <div className={'grid grid-cols-[2fr_1fr] gap-4'}>
                <Card className={'p-4'}>
                    <CardTitle className="text-lg">Return Items</CardTitle>

                    <CardContent className={'p-0'}>
                        <div className="space-y-4">
                            <div className="grid grid-cols-10 gap-4 text-sm font-semibold text-muted-foreground">
                                <div className="col-span-4">Name</div>
                                <div className="col-span-2 text-right">
                                    Price
                                </div>
                                <div className="col-span-2 text-right">
                                    Quantity
                                </div>
                                <div className="col-span-2 text-right">
                                    Total
                                </div>
                            </div>
                            <Separator />
                            {renderItems()}
                        </div>

                        <Separator className={'my-4'} />

                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between border-t pt-3">
                                <span className="font-semibold">Total:</span>
                                <span className="text-lg font-bold">
                                    {formatCurrencyInBDT(total ?? 0)}
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className={'p-4'}>
                    <CardTitle className="text-lg">Details</CardTitle>

                    <CardContent className={'p-0'}>
                        <div className={'flex justify-between'}>
                            <span>Return Date: </span>
                            <span>
                                {data?.data?.created_at
                                    ? format(
                                          data?.data?.created_at,
                                          'dd/MM/yyyy HH:mm a',
                                      )
                                    : '---'}
                            </span>
                        </div>

                        <div className={'flex justify-between'}>
                            <span>Invoice: </span>
                            <Link to={`${Route.SALES}/${data?.data?.order_id}`}>
                                <Button
                                    type={'button'}
                                    variant={'link'}
                                    size={'sm'}
                                    className={'p-0'}
                                >
                                    {data?.data?.invoice_number
                                        ? data?.data?.invoice_number
                                        : '---'}
                                </Button>
                            </Link>
                        </div>

                        <div className={'flex justify-between'}>
                            <span>Processed By: </span>
                            <Link to={`${Route.USERS}/${data?.data?.user_id}`}>
                                <Button
                                    type={'button'}
                                    variant={'link'}
                                    size={'sm'}
                                    className={'p-0'}
                                >
                                    {data?.data?.user
                                        ? data?.data?.user?.name
                                        : '---'}
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Card>
    );
};

SalesReturnDetails.displayName = 'SalesReturnDetails';
